import React, { useEffect, useState } from "react";
import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  AutocompleteInput,
  DateInput,
  FormDataConsumer,
} from "react-admin";
import {
  collection,
  addDoc,
  serverTimestamp,
  onSnapshot,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase/firebase";
import { DriverExpiryInputs } from "./DriverFields";
import * as XLSX from "xlsx";

export const DriverCreate = () => {
  const [companies, setCompanies] = useState([]);

  // 🔹 Fetch company list
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "companies"), (snap) => {
      const list = snap.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
      }));
      setCompanies(list);
    });
    return () => unsub();
  }, []);

  // 🔹 Helper to upload file & return URL
  const uploadFile = async (file, folder) => {
    if (!file || !file.rawFile) return null;
    const fileRef = ref(storage, `${folder}/${Date.now()}_${file.rawFile.name}`);
    await uploadBytes(fileRef, file.rawFile);
    return await getDownloadURL(fileRef);
  };

  // ====================================================================================
  // 🔹 BULK UPLOAD FROM EXCEL
  // ====================================================================================
  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      if (!rows.length) {
        alert("❌ Excel is empty.");
        return;
      }

      alert(`📄 Loaded ${rows.length} employees. Uploading...`);

      // Fetch existing company list
      const snap = await getDocs(collection(db, "companies"));
      const existingCompanies = snap.docs.map((d) => d.data().name);

      for (const r of rows) {
        const companyName = r.company?.trim() || "";

        if (!existingCompanies.includes(companyName)) {
  throw new Error(
    `❌ Company "${companyName}" does not match an existing company in Firebase. ` +
    `Please correct the Excel file to use exactly one of these: ${existingCompanies.join(", ")}`
  );
}


        // Save driver
        await addDoc(collection(db, "drivers"), {
          fullName: r.fullName || "",
          phone: r.phone || "",
          company: companyName,
          status: r.status || "Active",
          cancelledAt:
            String(r.status || "").toLowerCase() === "cancelled"
              ? (r.cancelledAt
                  ? Timestamp.fromDate(new Date(String(r.cancelledAt).slice(0, 10) + "T00:00:00"))
                  : serverTimestamp())
              : null,

          passportNumber: r.passportNumber || "",

          passportExpiry: r.passportExpiry || null,
          emiratesIdExpiry: r.emiratesIdExpiry || null,
          licenseExpiry: r.licenseExpiry || null,
          rtaPermitExpiry: r.rtaPermitExpiry || null,
          rtaTrainingExpiry: r.rtaTrainingExpiry || null,
          healthInsuranceExpiry: r.healthInsuranceExpiry || null,

          createdAt: serverTimestamp(),
          createdBy: localStorage.getItem("email") || "",
        });
      }

      alert("🎉 Bulk upload completed successfully!");
      window.location.hash = "#/drivers";
    } catch (err) {
      console.error(err);
      alert("❌ Bulk upload failed: " + err.message);
    }
  };

  // ====================================================================================
  // 🔹 SINGLE DRIVER CREATE
  // ====================================================================================
  const handleSave = async (values) => {
    try {
      let companyName = values.company;

      // Create company if new
      if (!companies.some((c) => c.name === companyName)) {
        await addDoc(collection(db, "companies"), { name: companyName });
      }

      // Upload document files
      const fileFields = [
        { key: "passportFile", folder: "drivers/passport" },
        { key: "passportFileBack", folder: "drivers/passport" },
        { key: "emiratesIdFile", folder: "drivers/emiratesId" },
        { key: "emiratesIdFileBack", folder: "drivers/emiratesId" },
        { key: "licenseFile", folder: "drivers/license" },
        { key: "licenseFileBack", folder: "drivers/license" },
        { key: "rtaPermitFile", folder: "drivers/rtaPermit" },
        { key: "rtaTrainingFile", folder: "drivers/training" },
        { key: "healthInsuranceFile", folder: "drivers/healthInsurance" },
      ];

      const uploadedFiles = {};
      for (const { key, folder } of fileFields) {
        const f = values[key]?.[0] || values[key]?.rawFile;
        uploadedFiles[key] = f ? await uploadFile(f, folder) : null;
      }

      // Save single driver
      await addDoc(collection(db, "drivers"), {
        fullName: values.fullName || "",
        phone: values.phone || "",
        company: companyName || "",
        status: values.status || "Active",
          cancelledAt:
            String(values.status || "").toLowerCase() === "cancelled"
              ? (values.cancelledAt
                  ? Timestamp.fromDate(new Date(String(values.cancelledAt).slice(0, 10) + "T00:00:00"))
                  : serverTimestamp())
              : null,

        passportExpiry: values.passportExpiry || null,
        emiratesIdExpiry: values.emiratesIdExpiry || null,
        licenseExpiry: values.licenseExpiry || null,
        rtaPermitExpiry: values.rtaPermitExpiry || null,
        rtaTrainingExpiry: values.rtaTrainingExpiry || null,
        healthInsuranceExpiry: values.healthInsuranceExpiry || null,

        ...uploadedFiles,

        createdAt: serverTimestamp(),
        createdBy: localStorage.getItem("email") || "",
      });

      alert("✅ Employee created successfully!");
      window.location.hash = "#/drivers";
    } catch (err) {
      console.error("❌ Error adding employee:", err);
      alert("❌ " + err.message);
    }
  };

  return (
    <Create title="Add New Employee">
      <>
        {/* ==================================================================================== */}
        {/* 🔹 BULK UPLOAD BUTTON */}
        {/* ==================================================================================== */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "20px",
          }}
        >
          <label
            style={{
              padding: "8px 14px",
              background: "#1976d2",
              color: "white",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Bulk Upload Excel
            <input
              type="file"
              accept=".xlsx"
              style={{ display: "none" }}
              onChange={handleBulkUpload}
            />
          </label>
        </div>

        {/* ==================================================================================== */}
        {/* 🔹 SINGLE DRIVER FORM */}
        {/* ==================================================================================== */}
        <SimpleForm onSubmit={handleSave}>
          <TextInput source="fullName" label="Full Name" fullWidth required />
          <TextInput source="phone" label="Phone Number" fullWidth required />

          <AutocompleteInput
            source="company"
            label="Company"
            choices={companies}
            optionText="name"
            optionValue="name"
            fullWidth
            helperText="Select company or type to create new"
          />

          <SelectInput
            source="status"
            label="Status"
            choices={[
              { id: "Active", name: "Active" },
              { id: "Cancelled", name: "Cancelled" },
            ]}
            defaultValue="Active"
          />

<FormDataConsumer>
  {({ formData }) =>
    String(formData?.status || "").toLowerCase() === "cancelled" ? (
      <DateInput
        source="cancelledAt"
        label="Cancelled Date"
        helperText="Optional. If empty, it will be set to today when saving."
        fullWidth
      />
    ) : null
  }
</FormDataConsumer>


          {/* Expiry + Upload fields */}
          <DriverExpiryInputs />
        </SimpleForm>
      </>
    </Create>
  );
};